import { getCurrentOperatorName } from '../../utils/currentOperator';
import React from 'react';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  BandEyebrow,
  BandHeader,
  BandSubline,
  BandTitle,
  BandTitleGroup,
  EconomicsPreview,
  ScopePill,
  StructuredBand,
  StructuredHeroPanel,
  StructuredWorkspace,
  TradeEconomicsGrid,
} from './shared/StructuredCreditTicketLayout';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { settlementCalendars } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';
import styled from 'styled-components';
import { BadgeDollarSign, Building2 } from 'lucide-react';

import { DateField } from '../shared/DateField';

const MetricsPanel = styled.div`
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.9) 100%);
  border: 1px solid rgba(51, 65, 85, 0.74);
  border-radius: 14px;
  padding: 16px;
  margin: 16px 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

const MetricCard = styled.div<{ $alert?: boolean }>`
  background: rgba(15, 23, 42, 0.6);
  padding: 12px;
  border-radius: 11px;
  border: 1px solid ${p => p.$alert ? 'rgba(245, 158, 11, 0.45)' : 'rgba(71, 85, 105, 0.36)'};
`;

const MetricLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const MetricValue = styled.div<{ $color?: string }>`
  font-size: 18px;
  font-weight: 850;
  color: ${p => p.$color ?? '#e2e8f0'};
`;

const MetricSub = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
`;

const SectionTitle = styled.h3`
  color: #f8fafc;
  font-size: 15px;
  font-weight: 850;
  margin: 24px 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(51, 65, 85, 0.74);
`;

// ──────────────────────────────────────────────────────────────────────
// Loan Structure — segmented control + subtype-aware body. Inline with
// the rest of the dark-theme form (no card chrome). Tabs surface only
// the rows/fields the chosen instrument_subtype actually needs.
// ──────────────────────────────────────────────────────────────────────

const StructureWrap = styled.div`
  margin-top: 12px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgba(55, 65, 81, 0.5);
  margin-bottom: 18px;
`;

const Tab = styled.button<{ $active?: boolean; $enabled?: boolean }>`
  padding: 10px 18px;
  font-size: 11px;
  font-weight: 600;
  background: transparent;
  color: ${p => !p.$enabled ? 'rgba(107, 114, 128, 0.5)' : p.$active ? '#3b82f6' : '#94a3b8'};
  border: none;
  border-bottom: 2px solid ${p => p.$active ? '#3b82f6' : 'transparent'};
  margin-bottom: -1px;
  cursor: ${p => p.$enabled ? 'pointer' : 'not-allowed'};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: ${p => p.$enabled && !p.$active ? '#cbd5e1' : undefined};
  }
`;

const TabBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 1px 7px;
  font-size: 10px;
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
  border-radius: 8px;
  letter-spacing: 0;
`;

const Helper = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 14px;
  line-height: 1.5;
`;

const SubLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 18px 0 8px;
`;

const Grid = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th {
    padding: 8px 8px;
    text-align: left;
    border-bottom: 1px solid rgba(55, 65, 81, 0.5);
    font-weight: 600;
    color: #94a3b8;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  td {
    padding: 6px 6px;
    border-bottom: 1px solid rgba(55, 65, 81, 0.3);
    vertical-align: middle;
  }
  tbody tr:hover td {
    background: rgba(26, 34, 53, 0.4);
  }
`;

const GridInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  font-size: 12px;
  background: rgba(15, 23, 42, 0.7);
  color: #f9fafb;
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 4px;
  font-family: inherit;
  &::placeholder { color: rgba(107, 114, 128, 0.7); }
  &:focus { outline: none; border-color: #3b82f6; background: rgba(15, 23, 42, 0.95); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const GridSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  font-size: 12px;
  background: rgba(15, 23, 42, 0.7);
  color: #f9fafb;
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 4px;
  font-family: inherit;
  &:focus { outline: none; border-color: #3b82f6; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  option { background: #111827; color: #f9fafb; }
`;

const AddBtn = styled.button`
  margin-top: 10px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.12);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.35);
  border-radius: 4px;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  transition: background 0.15s;
  &:hover { background: rgba(59, 130, 246, 0.22); }
`;

const DeleteBtn = styled.button`
  padding: 3px 9px;
  font-size: 14px;
  line-height: 1;
  background: transparent;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.35);
  border-radius: 4px;
  cursor: pointer;
  &:hover { background: rgba(239, 68, 68, 0.15); }
`;

const EmptyRow = styled.div`
  padding: 16px 4px;
  text-align: center;
  font-size: 12px;
  color: #6b7280;
  border: 1px dashed rgba(55, 65, 81, 0.4);
  border-radius: 6px;
  margin-bottom: 10px;
`;

type StructureTabKey = 'capital' | 'extension' | 'construction' | 'prefeq';

const isMezzyStack = (st?: string) =>
  st === 'MEZZANINE' || st === 'BRIDGE_MEZZ' || st === 'PREFERRED_EQUITY';
const isBridgeSubtype = (st?: string) =>
  st === 'BRIDGE_SENIOR' || st === 'BRIDGE_MEZZ';
const isConstructionSubtype = (st?: string) =>
  st === 'CONSTRUCTION';
const isPrefEqSubtype = (st?: string) =>
  st === 'PREFERRED_EQUITY';

const subtypeNeedsAnyTab = (st?: string) =>
  isMezzyStack(st) || isBridgeSubtype(st) || isConstructionSubtype(st) || isPrefEqSubtype(st);

interface LoanStructureTabsProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  disabled: boolean;
}

const LoanStructureTabs: React.FC<LoanStructureTabsProps> = ({ formData, setFormData, disabled }) => {
  const subtype: string = formData.instrumentSubtype || 'WHOLE_LOAN';
  const capEnabled = isMezzyStack(subtype);
  const extEnabled = isBridgeSubtype(subtype);
  const conEnabled = isConstructionSubtype(subtype);
  const prefEnabled = isPrefEqSubtype(subtype);

  // First-enabled tab as the default active.
  const firstEnabled: StructureTabKey =
    capEnabled ? 'capital' :
    extEnabled ? 'extension' :
    conEnabled ? 'construction' :
    prefEnabled ? 'prefeq' : 'capital';

  const [active, setActive] = React.useState<StructureTabKey>(firstEnabled);

  // If the subtype changes such that the active tab is no longer enabled,
  // jump to the first enabled tab. (Re-runs only on subtype change.)
  React.useEffect(() => {
    const enabledMap: Record<StructureTabKey, boolean> = {
      capital: capEnabled, extension: extEnabled,
      construction: conEnabled, prefeq: prefEnabled,
    };
    if (!enabledMap[active]) setActive(firstEnabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtype]);

  const updateRow = (key: 'capitalStack' | 'extensionOptionsList' | 'budgetLines',
                    idx: number, patch: Record<string, any>) => {
    setFormData((prev: any) => {
      const arr = Array.isArray(prev[key]) ? [...prev[key]] : [];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...prev, [key]: arr };
    });
  };
  const addRow = (key: 'capitalStack' | 'extensionOptionsList' | 'budgetLines',
                  template: Record<string, any>) => {
    setFormData((prev: any) => {
      const arr = Array.isArray(prev[key]) ? [...prev[key]] : [];
      return { ...prev, [key]: [...arr, template] };
    });
  };
  const deleteRow = (key: 'capitalStack' | 'extensionOptionsList' | 'budgetLines', idx: number) => {
    setFormData((prev: any) => {
      const arr = Array.isArray(prev[key]) ? [...prev[key]] : [];
      arr.splice(idx, 1);
      return { ...prev, [key]: arr };
    });
  };

  const capitalStack: any[] = formData.capitalStack || [];
  const extensions: any[] = formData.extensionOptionsList || [];
  const budgetLines: any[] = formData.budgetLines || [];

  return (
    <StructureWrap>
      <TabRow>
        <Tab type="button" $enabled={capEnabled} $active={active === 'capital'}
             onClick={() => capEnabled && setActive('capital')}>
          Capital Stack
          {capEnabled && capitalStack.length > 0 && <TabBadge>{capitalStack.length}</TabBadge>}
        </Tab>
        <Tab type="button" $enabled={extEnabled} $active={active === 'extension'}
             onClick={() => extEnabled && setActive('extension')}>
          Extension Options
          {extEnabled && extensions.length > 0 && <TabBadge>{extensions.length}</TabBadge>}
        </Tab>
        <Tab type="button" $enabled={conEnabled} $active={active === 'construction'}
             onClick={() => conEnabled && setActive('construction')}>
          Construction Budget
          {conEnabled && budgetLines.length > 0 && <TabBadge>{budgetLines.length}</TabBadge>}
        </Tab>
        <Tab type="button" $enabled={prefEnabled} $active={active === 'prefeq'}
             onClick={() => prefEnabled && setActive('prefeq')}>
          Pref-Equity Terms
        </Tab>
      </TabRow>

      {active === 'capital' && capEnabled && (
        <>
          <Helper>
            Syndicate / capital-stack siblings. Bilateral loans only need the
            two rows of the borrower's stack the lender holds (e.g. "this
            mezz" + the senior in front). Add one row per tranche.
          </Helper>
          {capitalStack.length === 0 && (
            <EmptyRow>No tranches yet — add the senior in front, this position, and any junior tranches.</EmptyRow>
          )}
          {capitalStack.length > 0 && (
            <Grid>
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Rank</th>
                  <th style={{ width: 130 }}>Position</th>
                  <th>Counter-Lender</th>
                  <th>External Loan Name</th>
                  <th style={{ width: 170 }}>Par Amount ($)</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {capitalStack.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.positionRank ?? ''}
                                 onChange={e => updateRow('capitalStack', idx, { positionRank: e.target.value })}
                                 placeholder="1=senior" />
                    </td>
                    <td>
                      <GridSelect disabled={disabled} value={row.positionLabel || ''}
                                  onChange={e => updateRow('capitalStack', idx, { positionLabel: e.target.value })}>
                        <option value="">—</option>
                        <option value="SENIOR">Senior</option>
                        <option value="SENIOR_A">Senior A</option>
                        <option value="SENIOR_B">Senior B</option>
                        <option value="MEZZ_A">Mezz A</option>
                        <option value="MEZZ_B">Mezz B</option>
                        <option value="MEZZ_C">Mezz C</option>
                        <option value="PREF_EQ">Pref Equity</option>
                      </GridSelect>
                    </td>
                    <td>
                      <GridInput type="text" disabled={disabled}
                                 value={row.relatedLenderName || ''}
                                 onChange={e => updateRow('capitalStack', idx, { relatedLenderName: e.target.value })}
                                 placeholder="e.g. Wells Fargo, Mack RE" />
                    </td>
                    <td>
                      <GridInput type="text" disabled={disabled}
                                 value={row.relatedLoanExternalName || ''}
                                 onChange={e => updateRow('capitalStack', idx, { relatedLoanExternalName: e.target.value })}
                                 placeholder="e.g. Senior Mortgage Note" />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.parAmount ?? ''}
                                 onChange={e => updateRow('capitalStack', idx, { parAmount: e.target.value })}
                                 placeholder="100000000" />
                    </td>
                    <td>
                      {!disabled && (
                        <DeleteBtn type="button" onClick={() => deleteRow('capitalStack', idx)}>×</DeleteBtn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Grid>
          )}
          {!disabled && (
            <AddBtn type="button"
                    onClick={() => addRow('capitalStack', {
                      positionRank: capitalStack.length + 1,
                      positionLabel: '',
                      relatedLenderName: '',
                      parAmount: '',
                    })}>
              + Add Tranche
            </AddBtn>
          )}
        </>
      )}

      {active === 'extension' && extEnabled && (
        <>
          <Helper>
            Bridge loans typically have one or two extension options
            (e.g. 2 × 12-month) with covenant tests at exercise. Each row
            describes one option.
          </Helper>
          {extensions.length === 0 && (
            <EmptyRow>No extension options yet — add the first option (e.g. 12 months @ 25 bps).</EmptyRow>
          )}
          {extensions.length > 0 && (
            <Grid>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th style={{ width: 90 }}>Months</th>
                  <th style={{ width: 110 }}>Fee (bps)</th>
                  <th style={{ width: 120 }}>Max LTV @ Ex</th>
                  <th style={{ width: 120 }}>Min DSCR @ Ex</th>
                  <th style={{ width: 130 }}>Min Debt Yield @ Ex</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {extensions.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.sequenceNum ?? ''}
                                 onChange={e => updateRow('extensionOptionsList', idx, { sequenceNum: e.target.value })} />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.extensionMonths ?? ''}
                                 onChange={e => updateRow('extensionOptionsList', idx, { extensionMonths: e.target.value })}
                                 placeholder="12" />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.extensionFeeBps ?? ''}
                                 onChange={e => updateRow('extensionOptionsList', idx, { extensionFeeBps: e.target.value })}
                                 placeholder="25" />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.maxLtvAtExercise ?? ''}
                                 onChange={e => updateRow('extensionOptionsList', idx, { maxLtvAtExercise: e.target.value })}
                                 placeholder="70" />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.minDscrAtExercise ?? ''}
                                 onChange={e => updateRow('extensionOptionsList', idx, { minDscrAtExercise: e.target.value })}
                                 placeholder="1.20" />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.minDebtYieldAtExercise ?? ''}
                                 onChange={e => updateRow('extensionOptionsList', idx, { minDebtYieldAtExercise: e.target.value })}
                                 placeholder="8.5" />
                    </td>
                    <td>
                      {!disabled && (
                        <DeleteBtn type="button" onClick={() => deleteRow('extensionOptionsList', idx)}>×</DeleteBtn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Grid>
          )}
          {!disabled && (
            <AddBtn type="button"
                    onClick={() => addRow('extensionOptionsList', {
                      sequenceNum: extensions.length + 1,
                      extensionMonths: 12,
                      extensionFeeBps: '',
                    })}>
              + Add Extension Option
            </AddBtn>
          )}
        </>
      )}

      {active === 'construction' && conEnabled && (
        <>
          <Helper>
            Construction-loan governance: capitalized interest reserve,
            completion guaranty, retainage, and the budget breakdown that
            draws (filed from the dashboard) draw against.
          </Helper>
          <FormRow>
            <FormGroup>
              <Label>Cap Interest Reserve ($)</Label>
              <Input type="text" disabled={disabled}
                     value={formData.capInterestReserveInitial || ''}
                     onChange={e => setFormData((p: any) => ({ ...p, capInterestReserveInitial: e.target.value }))}
                     onBlur={e => {
                       const parsed = parseAmountShorthand(e.target.value);
                       if (parsed > 0) setFormData((p: any) => ({ ...p, capInterestReserveInitial: formatAmountWithCommas(parsed) }));
                     }}
                     placeholder="e.g. 5M" />
            </FormGroup>
            <FormGroup>
              <Label>Retainage (%)</Label>
              <Input type="number" step="0.01" disabled={disabled}
                     value={formData.retainagePct || ''}
                     onChange={e => setFormData((p: any) => ({ ...p, retainagePct: e.target.value }))}
                     placeholder="e.g. 10" />
            </FormGroup>
            <FormGroup>
              <Label>In-Balance Test Status</Label>
              <Select disabled={disabled}
                      value={formData.inBalanceTestPass === 'true' || formData.inBalanceTestPass === true ? 'true' :
                             formData.inBalanceTestPass === 'false' || formData.inBalanceTestPass === false ? 'false' : ''}
                      onChange={e => setFormData((p: any) => ({ ...p, inBalanceTestPass: e.target.value }))}>
                <option value="">— Not yet tested —</option>
                <option value="true">Pass (in balance)</option>
                <option value="false">Fail (out of balance)</option>
              </Select>
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Completion Guaranty Party</Label>
              <Input type="text" disabled={disabled}
                     value={formData.completionGuarantyParty || ''}
                     onChange={e => setFormData((p: any) => ({ ...p, completionGuarantyParty: e.target.value }))}
                     placeholder="e.g. Sponsor Holdings LP" />
            </FormGroup>
            <FormGroup>
              <Label>Completion Guaranty Type</Label>
              <Select disabled={disabled}
                      value={formData.completionGuarantyType || ''}
                      onChange={e => setFormData((p: any) => ({ ...p, completionGuarantyType: e.target.value }))}>
                <option value="">—</option>
                <option value="FULL">Full Completion Guaranty</option>
                <option value="LIMITED">Limited (cost-overrun only)</option>
                <option value="CARVE_OUT">Carve-Out Only</option>
                <option value="NONE">None</option>
              </Select>
            </FormGroup>
          </FormRow>

          <SubLabel>Budget Lines</SubLabel>
          {budgetLines.length === 0 && (
            <EmptyRow>No budget lines yet — add Hard Costs, Soft Costs, Interest Reserve, etc.</EmptyRow>
          )}
          {budgetLines.length > 0 && (
            <Grid>
              <thead>
                <tr>
                  <th style={{ width: 200 }}>Category</th>
                  <th>Sub-Category</th>
                  <th style={{ width: 200 }}>Budgeted Amount ($)</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {budgetLines.map((row: any, idx: number) => (
                  <tr key={idx}>
                    <td>
                      <GridSelect disabled={disabled} value={row.category || ''}
                                  onChange={e => updateRow('budgetLines', idx, { category: e.target.value })}>
                        <option value="">—</option>
                        <option value="HARD_COSTS">Hard Costs</option>
                        <option value="SOFT_COSTS">Soft Costs</option>
                        <option value="LAND">Land</option>
                        <option value="INTEREST_RESERVE">Interest Reserve</option>
                        <option value="CONTINGENCY">Contingency</option>
                        <option value="TENANT_IMPROVEMENTS">Tenant Improvements</option>
                        <option value="LEASING_COMMISSIONS">Leasing Commissions</option>
                      </GridSelect>
                    </td>
                    <td>
                      <GridInput type="text" disabled={disabled}
                                 value={row.subCategory || ''}
                                 onChange={e => updateRow('budgetLines', idx, { subCategory: e.target.value })}
                                 placeholder="e.g. Vertical Construction, Architect Fees" />
                    </td>
                    <td>
                      <GridInput type="number" disabled={disabled}
                                 value={row.budgetedAmount ?? ''}
                                 onChange={e => updateRow('budgetLines', idx, { budgetedAmount: e.target.value })}
                                 placeholder="32000000" />
                    </td>
                    <td>
                      {!disabled && (
                        <DeleteBtn type="button" onClick={() => deleteRow('budgetLines', idx)}>×</DeleteBtn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Grid>
          )}
          {!disabled && (
            <AddBtn type="button"
                    onClick={() => addRow('budgetLines', {
                      category: 'HARD_COSTS',
                      subCategory: '',
                      budgetedAmount: '',
                    })}>
              + Add Budget Line
            </AddBtn>
          )}
        </>
      )}

      {active === 'prefeq' && prefEnabled && (
        <>
          <Helper>
            Preferred-equity-specific terms. Pref-eq is held in the RE Debt
            lane (with <code>instrument_subtype = PREFERRED_EQUITY</code>) but
            skips LTV / DSCR rollups since it sits behind debt in the stack.
          </Helper>
          <FormRow>
            <FormGroup>
              <Label>Preferred Return Rate (%)</Label>
              <Input type="number" step="0.01" disabled={disabled}
                     value={formData.preferredReturnRate || ''}
                     onChange={e => setFormData((p: any) => ({ ...p, preferredReturnRate: e.target.value }))}
                     placeholder="e.g. 12.00" />
            </FormGroup>
            <FormGroup>
              <Label>Accrual Method</Label>
              <Select disabled={disabled}
                      value={formData.accrualMethod || ''}
                      onChange={e => setFormData((p: any) => ({ ...p, accrualMethod: e.target.value }))}>
                <option value="">—</option>
                <option value="CURRENT_PAY">Current Pay (cash)</option>
                <option value="ACCRUE">Accrue (PIK)</option>
                <option value="HYBRID">Hybrid (split current/accrual)</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Waterfall Position</Label>
              <Input type="number" disabled={disabled}
                     value={formData.waterfallPosition ?? ''}
                     onChange={e => setFormData((p: any) => ({ ...p, waterfallPosition: e.target.value }))}
                     placeholder="e.g. 3" />
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>Mandatory Redemption Date</Label>
              <DateField
                disabled={disabled}
                value={formData.mandatoryRedemptionDate || ''}
                onChange={iso => setFormData((p: any) => ({ ...p, mandatoryRedemptionDate: iso }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>Step-Up After Date</Label>
              <DateField
                disabled={disabled}
                value={formData.stepUpAfterDate || ''}
                onChange={iso => setFormData((p: any) => ({ ...p, stepUpAfterDate: iso }))}
              />
            </FormGroup>
            <FormGroup>
              <Label>Step-Up Rate (%)</Label>
              <Input type="number" step="0.01" disabled={disabled}
                     value={formData.stepUpRate || ''}
                     onChange={e => setFormData((p: any) => ({ ...p, stepUpRate: e.target.value }))}
                     placeholder="e.g. 18.00" />
            </FormGroup>
          </FormRow>
        </>
      )}

      {!subtypeNeedsAnyTab(subtype) && (
        <Helper style={{ margin: 0, padding: '12px 4px', textAlign: 'center' }}>
          Whole-loan / permanent / CMBS-legacy structures don't have additional
          tab fields. Switch <strong>Instrument Subtype</strong> to Mezzanine,
          Bridge, Construction, or Pref-Equity to surface their structure tabs.
        </Helper>
      )}
    </StructureWrap>
  );
};

/**
 * Real Estate Debt (commercial mortgage) trade entry.
 *
 * Acquisition-only ticket — paydown / rate-reset / amendment / writedown /
 * payoff originate from the RE Debt position dashboard, not this screen.
 *
 * Two-step flow:
 *  1. POST /api/credit/re-debt/prepare → creates real_estate_loan + synthetic
 *     dim_security_master, returns {loanId, securityId, cusip, sourceRef}.
 *  2. POST /api/trade-transactions with ticketType=RE_DEBT, eventType=ACQUIRE,
 *     sourceRef. Trade enters DRAFT and traverses the standard OMS lifecycle
 *     (DRAFT → PENDING_PRECLEAR → APPROVED → SENT → EXECUTED → POSTED).
 *  3. At POSTED, LoanPostingSpi mirrors the loan into fact_positions +
 *     fact_position_loan_attrs (loan_source=RE_DEBT).
 */
const RealEstateTicketScreen: React.FC = () => {
  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();

  const initialFormData = {
    // RE Debt ticket is acquisition-only; lifecycle events come from dashboard.
    transactionType: 'BUY' as const,
    // No "Default Asset Manager" sentinel — that string never matched a real
    // dim_asset_manager row, the dropdown silently rendered option[0] over it,
    // and the user's picks were discarded on submit-for-pre-clear (see
    // BaseTicketScreen handleSubmit + V286).
    assetManager: pendingTrade?.assetManager || '',
    portfolioManager: pendingTrade?.portfolioManager || '',
    assetClass: 'Real Estate',
    assetSubClass: 'RE Debt - Senior Mortgage',
    security: pendingTrade?.security || pendingTrade?.loanName || '',
    identifier: pendingTrade?.identifier || pendingTrade?.cusip || '',
    portfolio: pendingTrade?.portfolio || pendingTrade?.portfolioName || '',
    amount: pendingTrade?.amount || pendingTrade?.fundedAmount || '',
    tradeDate: pendingTrade?.tradeDate || getTodayDate(),
    settleDate: pendingTrade?.settleDate || pendingTrade?.tradeDate || getTodayDate(),
    submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
    currency: pendingTrade?.currency || 'USD',
    executedBy: pendingTrade?.executedBy || '',
    notes: pendingTrade?.notes || '',

    // Loan identification
    loanName: pendingTrade?.loanName || '',
    loanNumber: pendingTrade?.loanNumber || '',
    loanType: pendingTrade?.loanType || 'SENIOR',
    instrumentSubtype: pendingTrade?.instrumentSubtype || 'WHOLE_LOAN',
    isConstruction: pendingTrade?.isConstruction ? 'true' : 'false',
    // V299 typed classification discriminators
    collateralType: pendingTrade?.collateralType || 'FIRST_MORTGAGE_LIEN',
    gaapClassification: pendingTrade?.gaapClassification || 'DEBT',
    payMechanic: pendingTrade?.payMechanic || 'CASH_PAY',

    // Counterparties
    sponsorName: pendingTrade?.sponsorName || '',
    guarantorName: pendingTrade?.guarantorName || '',
    servicerName: pendingTrade?.servicerName || '',
    specialServicer: pendingTrade?.specialServicer || '',

    // Economics
    originalPrincipal: pendingTrade?.originalPrincipal || '',
    totalCommitment: pendingTrade?.totalCommitment || '',
    fundedAmount: pendingTrade?.fundedAmount || '',
    constructionBudget: pendingTrade?.constructionBudget || '',

    // Rate structure
    rateType: pendingTrade?.rateType || 'FLOATING',
    interestRate: pendingTrade?.interestRate || '',
    spreadBps: pendingTrade?.spreadBps || '',
    indexRate: pendingTrade?.indexRate || 'SOFR',
    floorRate: pendingTrade?.floorRate || '',

    // Dates
    originationDate: pendingTrade?.originationDate || getTodayDate(),
    maturityDate: pendingTrade?.maturityDate || '',
    completionDate: pendingTrade?.completionDate || '',
    extensionOptions: pendingTrade?.extensionOptions || '',

    // Covenants
    maxLtvCovenant: pendingTrade?.maxLtvCovenant || '',
    minDscrCovenant: pendingTrade?.minDscrCovenant || '',

    // Property collateral
    propertyName: pendingTrade?.propertyName || '',
    propertyType: pendingTrade?.propertyType || '',
    propertyAddress: pendingTrade?.propertyAddress || '',
    propertyCity: pendingTrade?.propertyCity || '',
    propertyState: pendingTrade?.propertyState || '',
    propertyZip: pendingTrade?.propertyZip || '',
    squareFootage: pendingTrade?.squareFootage || '',
    unitCount: pendingTrade?.unitCount || '',
    appraisedValue: pendingTrade?.appraisedValue || '',
    appraisalDate: pendingTrade?.appraisalDate || '',
    appraiser: pendingTrade?.appraiser || '',

    // Subtype-specific scalar terms (surfaced in the Loan Structure tabs)
    capInterestReserveInitial: pendingTrade?.capInterestReserveInitial || '',
    completionGuarantyParty: pendingTrade?.completionGuarantyParty || '',
    completionGuarantyType: pendingTrade?.completionGuarantyType || '',
    retainagePct: pendingTrade?.retainagePct || '',
    inBalanceTestPass: pendingTrade?.inBalanceTestPass ?? '',
    preferredReturnRate: pendingTrade?.preferredReturnRate || '',
    accrualMethod: pendingTrade?.accrualMethod || '',
    mandatoryRedemptionDate: pendingTrade?.mandatoryRedemptionDate || '',
    stepUpAfterDate: pendingTrade?.stepUpAfterDate || '',
    stepUpRate: pendingTrade?.stepUpRate || '',
    waterfallPosition: pendingTrade?.waterfallPosition || '',

    // Subtype-specific child rows (variable-length arrays)
    capitalStack: Array.isArray(pendingTrade?.capitalStack) ? pendingTrade.capitalStack : [],
    extensionOptionsList: Array.isArray(pendingTrade?.extensionOptionsList) ? pendingTrade.extensionOptionsList : [],
    budgetLines: Array.isArray(pendingTrade?.budgetLines) ? pendingTrade.budgetLines : [],
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.loanName?.trim()) errors.loanName = 'Loan Name is required';
    if (!formData.loanType) errors.loanType = 'Loan Type is required';
    if (!formData.fundedAmount) errors.fundedAmount = 'Funded Amount is required';
    if (formData.fundedAmount && parseAmountShorthand(formData.fundedAmount) <= 0)
      errors.fundedAmount = 'Must be > 0';
    if (!formData.maturityDate) errors.maturityDate = 'Maturity Date is required';
    if (!formData.originationDate) errors.originationDate = 'Origination Date is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';
    if (!formData.propertyName?.trim()) errors.propertyName = 'Property Name is required';
    if (!formData.propertyType) errors.propertyType = 'Property Type is required';

    if (formData.rateType === 'FLOATING') {
      if (!formData.indexRate) errors.indexRate = 'Index is required for floating rate';
      if (!formData.spreadBps) errors.spreadBps = 'Spread (bps) is required for floating rate';
    }
    if (formData.rateType === 'FIXED') {
      if (!formData.interestRate) errors.interestRate = 'Interest Rate is required for fixed rate';
    }

    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find(
      (p: any) => p.portfolioName === formData.portfolio.trim()
    );
    if (!portfolio) {
      throw new Error(`Portfolio "${formData.portfolio}" not found`);
    }

    const fundedAmount = parseAmountShorthand(formData.fundedAmount);
    const totalCommitmentNum = formData.totalCommitment
      ? parseAmountShorthand(formData.totalCommitment)
      : fundedAmount;
    const originalPrincipalNum = formData.originalPrincipal
      ? parseAmountShorthand(formData.originalPrincipal)
      : fundedAmount;
    const appraisedValueNum = formData.appraisedValue ? parseAmountShorthand(formData.appraisedValue) : null;

    // Derive original LTV / DSCR from inputs (silo can later refine via doc ingestion).
    const originalLtv = (fundedAmount && appraisedValueNum && appraisedValueNum > 0)
      ? (fundedAmount / appraisedValueNum) * 100
      : null;

    // Step 1 — silo write + synthetic SMF. Returns the sourceRef the trade
    // payload below must echo so LoanPostingSpi can mirror the loan into
    // fact_positions / fact_position_loan_attrs at POST time.
    const preparePayload: Record<string, any> = {
      portfolioId: portfolio.id,
      loanName: formData.loanName.trim(),
      loanNumber: formData.loanNumber || null,
      loanType: formData.loanType,
      instrumentSubtype: formData.instrumentSubtype || 'WHOLE_LOAN',
      isConstruction: formData.isConstruction === 'true' || formData.isConstruction === true,
      // V299 typed classification discriminators
      collateralType: formData.collateralType || 'FIRST_MORTGAGE_LIEN',
      gaapClassification: formData.gaapClassification || 'DEBT',
      payMechanic: formData.payMechanic || 'CASH_PAY',
      sponsorName: formData.sponsorName || null,
      guarantorName: formData.guarantorName || null,
      servicerName: formData.servicerName || null,
      specialServicer: formData.specialServicer || null,
      originalPrincipal: originalPrincipalNum,
      currentPrincipal: fundedAmount,
      totalCommitment: totalCommitmentNum,
      fundedAmount: fundedAmount,
      constructionBudget: formData.constructionBudget ? parseAmountShorthand(formData.constructionBudget) : null,
      rateType: formData.rateType,
      interestRate: formData.interestRate ? parseFloat(formData.interestRate) : null,
      spreadBps: formData.spreadBps ? parseFloat(formData.spreadBps) : null,
      indexRate: formData.rateType === 'FLOATING' ? formData.indexRate : null,
      floorRate: formData.floorRate ? parseFloat(formData.floorRate) : null,
      originationDate: formData.originationDate,
      maturityDate: formData.maturityDate,
      completionDate: formData.completionDate || null,
      extensionOptions: formData.extensionOptions || null,
      originalLtv: originalLtv,
      maxLtvCovenant: formData.maxLtvCovenant ? parseFloat(formData.maxLtvCovenant) : null,
      minDscrCovenant: formData.minDscrCovenant ? parseFloat(formData.minDscrCovenant) : null,
      propertyName: formData.propertyName.trim(),
      propertyType: formData.propertyType,
      propertyAddress: formData.propertyAddress || null,
      propertyCity: formData.propertyCity || null,
      propertyState: formData.propertyState || null,
      propertyZip: formData.propertyZip || null,
      squareFootage: formData.squareFootage ? parseInt(formData.squareFootage, 10) : null,
      unitCount: formData.unitCount ? parseInt(formData.unitCount, 10) : null,
      appraisedValue: appraisedValueNum,
      appraisalDate: formData.appraisalDate || null,
      appraiser: formData.appraiser || null,

      // ── Subtype-specific scalar terms (surfaced via Loan Structure tabs) ──
      capInterestReserveInitial: formData.capInterestReserveInitial
          ? parseAmountShorthand(formData.capInterestReserveInitial) : null,
      completionGuarantyParty: formData.completionGuarantyParty || null,
      completionGuarantyType: formData.completionGuarantyType || null,
      retainagePct: formData.retainagePct ? parseFloat(formData.retainagePct) : null,
      inBalanceTestPass:
          formData.inBalanceTestPass === 'true' ? true :
          formData.inBalanceTestPass === 'false' ? false : null,
      preferredReturnRate: formData.preferredReturnRate
          ? parseFloat(formData.preferredReturnRate) : null,
      accrualMethod: formData.accrualMethod || null,
      mandatoryRedemptionDate: formData.mandatoryRedemptionDate || null,
      stepUpAfterDate: formData.stepUpAfterDate || null,
      stepUpRate: formData.stepUpRate ? parseFloat(formData.stepUpRate) : null,
      waterfallPosition: formData.waterfallPosition
          ? parseInt(formData.waterfallPosition, 10) : null,

      // ── Subtype-specific child rows (server persists after main row saves) ──
      capitalStack: Array.isArray(formData.capitalStack)
        ? formData.capitalStack
            .filter((r: any) => r && r.positionLabel && r.positionRank)
            .map((r: any) => ({
              positionRank: parseInt(r.positionRank, 10),
              positionLabel: r.positionLabel,
              relatedLenderName: r.relatedLenderName || null,
              relatedLoanExternalName: r.relatedLoanExternalName || null,
              parAmount: r.parAmount ? parseFloat(r.parAmount) : null,
            }))
        : [],
      extensionOptionsList: Array.isArray(formData.extensionOptionsList)
        ? formData.extensionOptionsList
            .filter((r: any) => r && r.sequenceNum && r.extensionMonths)
            .map((r: any) => ({
              sequenceNum: parseInt(r.sequenceNum, 10),
              extensionMonths: parseInt(r.extensionMonths, 10),
              extensionFeeBps: r.extensionFeeBps ? parseFloat(r.extensionFeeBps) : null,
              maxLtvAtExercise: r.maxLtvAtExercise ? parseFloat(r.maxLtvAtExercise) : null,
              minDscrAtExercise: r.minDscrAtExercise ? parseFloat(r.minDscrAtExercise) : null,
              minDebtYieldAtExercise: r.minDebtYieldAtExercise
                  ? parseFloat(r.minDebtYieldAtExercise) : null,
            }))
        : [],
      budgetLines: Array.isArray(formData.budgetLines)
        ? formData.budgetLines
            .filter((r: any) => r && r.category && r.budgetedAmount)
            .map((r: any) => ({
              category: r.category,
              subCategory: r.subCategory || null,
              budgetedAmount: parseFloat(r.budgetedAmount),
            }))
        : [],
    };

    const prepareRes = await axios.post('/api/credit/re-debt/prepare', preparePayload);
    const { securityId, sourceRef } = prepareRes.data;

    // Resolve dropdown names → dimension ids.
    let assetManagerId: number | null = null;
    if (formData.assetManager && formData.assetManager.trim()) {
      const am = dimensionData.assetManagers.find(
        (a: any) => a.assetManagerName === formData.assetManager.trim()
      );
      if (am?.id) assetManagerId = am.id;
    }
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find(
        (p: any) => p.portfolioManagerName === formData.portfolioManager.trim()
      );
      if (pm?.id) portfolioManagerId = pm.id;
    }

    // Step 2 — DRAFT trade. quantity=fundedAmount, price=100 (par origination).
    return {
      transactionType: 'BUY',
      securityId: securityId,
      portfolioId: portfolio.id,
      assetManagerId: assetManagerId,
      portfolioManagerId: portfolioManagerId,
      quantity: fundedAmount,
      price: 100,
      amount: fundedAmount,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || `RE Debt origination — ${formData.loanName}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType: 'RE_DEBT',
      eventType: 'ACQUIRE',
      sourceRef: sourceRef,
      currency: formData.currency || 'USD',
    };
  };

  const loadTransactionData = async (transaction: any) => {
    // Strip the " (TYPE)" suffix added to the synthetic security name.
    const loanName = (transaction.securityName || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
    const loadedTransactionType = transaction.transactionType || 'BUY';

    // Re-hydrate RE Debt loan terms from the silo. The trade itself only
    // carries lifecycle scalars + securityId; loan structure (instrument
    // subtype, sponsor, sizing, rate, covenants) and child rows
    // (capital stack / extension options / construction budget) live on
    // real_estate_loan and its child tables. Without this lookup the form
    // defaults instrument_subtype back to WHOLE_LOAN on reopen and the
    // Loan Structure tabs lose their saved data. Mirrors the portco-debt
    // by-security pattern.
    const securityId = transaction.securityId
      ?? transaction.security?.id
      ?? null;

    let loan: any = null;
    let capitalStack: any[] = [];
    let extensionOptionsList: any[] = [];
    let budgetLines: any[] = [];
    if (securityId) {
      try {
        const r = await axios.get(`/api/credit/real-estate/by-security/${securityId}`);
        loan = r.data?.loan ?? null;
        capitalStack = Array.isArray(r.data?.capitalStack) ? r.data.capitalStack : [];
        extensionOptionsList = Array.isArray(r.data?.extensionOptionsList) ? r.data.extensionOptionsList : [];
        budgetLines = Array.isArray(r.data?.budgetLines) ? r.data.budgetLines : [];
      } catch {
        // Silo not found — fall through to whatever the trade carries.
      }
    }

    // Helper: prefer silo value, then trade payload, then default.
    const pickStr = (siloVal: any, txVal: any, dflt = '') => {
      if (siloVal != null && siloVal !== '') return String(siloVal);
      if (txVal != null && txVal !== '') return String(txVal);
      return dflt;
    };
    const pickNumStr = (siloVal: any, txVal: any) => {
      if (siloVal != null) return siloVal.toString();
      if (txVal != null) return txVal.toString();
      return '';
    };

    return {
      transactionType: loadedTransactionType,
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: transaction.assetClassName || 'Real Estate',
      assetSubClass: transaction.assetSubClassName || 'RE Debt - Senior Mortgage',
      security: transaction.securityName || '',
      identifier: transaction.cusip || transaction.isin || transaction.identifier || '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      amount: transaction.amount ? transaction.amount.toString() : '',
      tradeDate: transaction.tradeDate || getTodayDate(),
      settleDate: transaction.settleDate || transaction.tradeDate || getTodayDate(),
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      currency: transaction.currency || loan?.currency || 'USD',
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      // RE Debt-specific — silo is the source of truth.
      loanName: loan?.loanName || loanName,
      loanNumber: pickStr(loan?.loanNumber, transaction.loanNumber),
      loanType: pickStr(loan?.loanType, transaction.loanType, 'SENIOR'),
      instrumentSubtype: pickStr(loan?.instrumentSubtype, transaction.instrumentSubtype, 'WHOLE_LOAN'),
      isConstruction: loan?.isConstruction ?? transaction.isConstruction ?? false,
      // V299 typed classification discriminators (silo first)
      collateralType: pickStr(loan?.collateralType, transaction.collateralType, 'FIRST_MORTGAGE_LIEN'),
      gaapClassification: pickStr(loan?.gaapClassification, transaction.gaapClassification, 'DEBT'),
      payMechanic: pickStr(loan?.payMechanic, transaction.payMechanic, 'CASH_PAY'),
      // Subtype scalar fields (silo first)
      capInterestReserveInitial: pickNumStr(loan?.capInterestReserveInitial, transaction.capInterestReserveInitial),
      completionGuarantyParty: pickStr(loan?.completionGuarantyParty, transaction.completionGuarantyParty),
      completionGuarantyType: pickStr(loan?.completionGuarantyType, transaction.completionGuarantyType),
      retainagePct: pickNumStr(loan?.retainagePct, transaction.retainagePct),
      inBalanceTestPass:
        loan?.inBalanceTestPass != null ? String(loan.inBalanceTestPass)
        : transaction.inBalanceTestPass == null ? '' : String(transaction.inBalanceTestPass),
      preferredReturnRate: pickNumStr(loan?.preferredReturnRate, transaction.preferredReturnRate),
      accrualMethod: pickStr(loan?.accrualMethod, transaction.accrualMethod),
      mandatoryRedemptionDate: pickStr(loan?.mandatoryRedemptionDate, transaction.mandatoryRedemptionDate),
      stepUpAfterDate: pickStr(loan?.stepUpAfterDate, transaction.stepUpAfterDate),
      stepUpRate: pickNumStr(loan?.stepUpRate, transaction.stepUpRate),
      waterfallPosition: pickNumStr(loan?.waterfallPosition, transaction.waterfallPosition),
      // Subtype child rows from silo (mapped to the form's row shape)
      capitalStack: capitalStack.map((r: any) => ({
        positionRank: r.positionRank != null ? String(r.positionRank) : '',
        positionLabel: r.positionLabel || '',
        relatedLenderName: r.relatedLenderName || '',
        relatedLoanExternalName: r.relatedLoanExternalName || '',
        parAmount: r.parAmount != null ? String(r.parAmount) : '',
      })),
      extensionOptionsList: extensionOptionsList.map((r: any) => ({
        sequenceNum: r.sequenceNum != null ? String(r.sequenceNum) : '',
        extensionMonths: r.extensionMonths != null ? String(r.extensionMonths) : '',
        extensionFeeBps: r.extensionFeeBps != null ? String(r.extensionFeeBps) : '',
        maxLtvAtExercise: r.maxLtvAtExercise != null ? String(r.maxLtvAtExercise) : '',
        minDscrAtExercise: r.minDscrAtExercise != null ? String(r.minDscrAtExercise) : '',
        minDebtYieldAtExercise: r.minDebtYieldAtExercise != null ? String(r.minDebtYieldAtExercise) : '',
      })),
      budgetLines: budgetLines.map((r: any) => ({
        category: r.category || '',
        subCategory: r.subCategory || '',
        budgetedAmount: r.budgetedAmount != null ? String(r.budgetedAmount) : '',
      })),
      sponsorName: pickStr(loan?.sponsorName, transaction.sponsorName),
      guarantorName: pickStr(loan?.guarantorName, transaction.guarantorName),
      servicerName: pickStr(loan?.servicerName, transaction.servicerName),
      specialServicer: pickStr(loan?.specialServicer, transaction.specialServicer),
      originalPrincipal: pickNumStr(loan?.originalPrincipal, transaction.originalPrincipal),
      totalCommitment: pickNumStr(loan?.totalCommitment, transaction.totalCommitment),
      // Form's "fundedAmount" is the trade size (the commitment booked on this
      // ticket). For construction loans the silo stores funded_amount=0 at
      // origination because nothing has been drawn yet — that's the right
      // accounting state but it's NOT what this form field represents.
      // Prefer the trade's amount (the original ticket size), fall back to
      // total_commitment, then to silo funded_amount only as a last resort.
      fundedAmount: pickNumStr(
        transaction.amount,
        loan?.totalCommitment != null && Number(loan.totalCommitment) > 0
          ? loan.totalCommitment
          : loan?.fundedAmount
      ),
      constructionBudget: pickNumStr(loan?.constructionBudget, transaction.constructionBudget),
      rateType: pickStr(loan?.rateType, transaction.rateType, 'FLOATING'),
      interestRate: pickNumStr(loan?.interestRate, transaction.interestRate),
      spreadBps: pickNumStr(loan?.spreadBps, transaction.spreadBps),
      indexRate: pickStr(loan?.indexRate, transaction.indexRate, 'SOFR'),
      floorRate: pickNumStr(loan?.floorRate, transaction.floorRate),
      originationDate: pickStr(loan?.originationDate, transaction.originationDate, transaction.tradeDate || getTodayDate()),
      maturityDate: pickStr(loan?.maturityDate, transaction.maturityDate),
      completionDate: pickStr(loan?.completionDate, transaction.completionDate),
      extensionOptions: pickStr(loan?.extensionOptions, transaction.extensionOptions),
      maxLtvCovenant: pickNumStr(loan?.maxLtvCovenant, transaction.maxLtvCovenant),
      minDscrCovenant: pickNumStr(loan?.minDscrCovenant, transaction.minDscrCovenant),
      propertyName: pickStr(loan?.propertyName, transaction.propertyName),
      propertyType: pickStr(loan?.propertyType, transaction.propertyType),
      propertyAddress: pickStr(loan?.propertyAddress, transaction.propertyAddress),
      propertyCity: pickStr(loan?.propertyCity, transaction.propertyCity),
      propertyState: pickStr(loan?.propertyState, transaction.propertyState),
      propertyZip: pickStr(loan?.propertyZip, transaction.propertyZip),
      squareFootage: pickNumStr(loan?.squareFootage, transaction.squareFootage),
      unitCount: pickNumStr(loan?.unitCount, transaction.unitCount),
      appraisedValue: pickNumStr(loan?.appraisedValue, transaction.appraisedValue),
      appraisalDate: pickStr(loan?.appraisalDate, transaction.appraisalDate),
      appraiser: pickStr(loan?.appraiser, transaction.appraiser),
    };
  };

  return (
    <BaseTicketScreen
      title="Real Estate Debt Trade Entry"
      assetClass="Real Estate"
      assetSubClass="RE Debt - Senior Mortgage"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur }) => {
        const fundedAmount = formData.fundedAmount ? parseAmountShorthand(formData.fundedAmount) : null;
        const appraisedValue = formData.appraisedValue ? parseAmountShorthand(formData.appraisedValue) : null;
        const interestRate = formData.interestRate ? parseFloat(formData.interestRate) : null;
        const spreadBps = formData.spreadBps ? parseFloat(formData.spreadBps) : null;

        const ltv = appraisedValue && fundedAmount && appraisedValue > 0
          ? (fundedAmount / appraisedValue) * 100 : null;

        // All-in rate: fixed = interestRate; floating = index + spread (display-only).
        const allInRate = formData.rateType === 'FIXED'
          ? interestRate
          : (interestRate ?? (spreadBps ? spreadBps / 100 : null));

        const showMetrics = ltv != null || allInRate != null;

        return (
          <StructuredWorkspace>
            <StructuredHeroPanel
              toneName="cml"
              icon={Building2}
              eyebrow="Real Estate"
              title="Commercial Mortgage Loan"
              description="Originate a commercial real estate loan with collateral, rate, covenants, subtype structure, and par-funded economics in one workflow."
              badges={['CML', 'Par origination', 'IBOR posted']}
              signals={[
                { label: 'Loan', value: formData.loanName || 'No loan', accent: !!formData.loanName },
                { label: 'Subtype', value: formData.instrumentSubtype || '-' },
                { label: 'Property', value: formData.propertyName || '-' },
                { label: 'Currency', value: formData.currency || 'USD' },
              ]}
            />

            <TradeEconomicsGrid>
              <StructuredBand>
                <BandHeader>
                  <BandTitleGroup>
                    <BandEyebrow $tone="cml"><BadgeDollarSign size={14} /> Loan Origination</BandEyebrow>
                    <BandTitle>Borrower, Collateral, Rate, And Structure</BandTitle>
                    <BandSubline>Commercial mortgage acquisition ticket with subtype-specific loan structure captured before posting.</BandSubline>
                  </BandTitleGroup>
                  <ScopePill $tone="cml">BUY</ScopePill>
                </BandHeader>
            {/* ── Row 1: Transaction Type, Trade Date, Settle Date ── */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="transactionType">Transaction Type*</Label>
                <Select
                  id="transactionType"
                  value={formData.transactionType || 'BUY'}
                  onChange={() => { /* read-only: lifecycle from dashboard */ }}
                  disabled
                >
                  <option value="BUY">Acquire (Originate)</option>
                  <option value="SELL">Payoff / Sale (from dashboard)</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="tradeDate">Trade Date*</Label>
                <DateField
                  id="tradeDate"
                  value={formData.tradeDate ?? ''}
                  className={errors.tradeDate ? 'error' : ''}
                  disabled={isFieldDisabled('tradeDate')}
                  calendars={calendarsFor('RE_DEBT', formData.currency)}
                  onChange={iso => { handleInputChange('tradeDate', iso); handleInputBlur('tradeDate', iso); }}
                />
                {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="settleDate">Settle Date*</Label>
                <DateField
                  id="settleDate"
                  value={formData.settleDate ?? ''}
                  className={errors.settleDate ? 'error' : ''}
                  disabled={isFieldDisabled('settleDate')}
                  calendars={settlementCalendars({ ticketType: 'RE_DEBT', currency: formData.currency })}
                  onChange={iso => handleInputChange('settleDate', iso)}
                />
                {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
              </FormGroup>
            </FormRow>

            {/* ── Row 2: Asset Manager, Portfolio Manager, Executed By ── */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="assetManager">Asset Manager*</Label>
                <Select
                  id="assetManager"
                  value={formData.assetManager}
                  onChange={(e) => handleInputChange('assetManager', e.target.value)}
                  disabled={isFieldDisabled('assetManager')}
                >
                  <option value="">Select Asset Manager</option>
                  {dimensionData.assetManagers.map((am: any) => (
                    <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
                <Select
                  id="portfolioManager"
                  value={formData.portfolioManager}
                  onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                  disabled={isFieldDisabled('portfolioManager')}
                >
                  <option value="">Select Portfolio Manager</option>
                  {dimensionData.portfolioManagers.map((pm: any) => (
                    <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                <Select
                  id="executedBy"
                  value={formData.executedBy}
                  onChange={(e) => handleInputChange('executedBy', e.target.value)}
                  disabled={isFieldDisabled('executedBy')}
                  className={errors.executedBy ? 'error' : ''}
                >
                  <option value="">Select</option>
                  {dimensionData.users.map((user: any) => (
                    <option key={user.id} value={user.fullName}>
                      {user.fullName}{user.role ? ` (${user.role})` : ''}
                    </option>
                  ))}
                </Select>
                {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
              </FormGroup>
            </FormRow>

            {/* ── Loan Identification ── */}
            <SectionTitle>Loan Identification</SectionTitle>
            <FormRow>
              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="loanName">Loan Name*</Label>
                <Input
                  id="loanName"
                  type="text"
                  value={formData.loanName}
                  onChange={(e) => {
                    handleInputChange('loanName', e.target.value);
                    handleInputChange('security', e.target.value);
                  }}
                  onBlur={(e) => handleInputBlur('loanName', e.target.value)}
                  placeholder="e.g. 100 Federal Street Senior Mortgage"
                  className={errors.loanName ? 'error' : ''}
                  disabled={isFieldDisabled('loanName')}
                />
                {errors.loanName && <FieldError>{errors.loanName}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="loanNumber">Loan Number</Label>
                <Input
                  id="loanNumber"
                  type="text"
                  value={formData.loanNumber}
                  onChange={(e) => handleInputChange('loanNumber', e.target.value)}
                  placeholder="e.g. CRE-2026-00123"
                  disabled={isFieldDisabled('loanNumber')}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="loanType">Loan Type*</Label>
                <Select
                  id="loanType"
                  value={formData.loanType}
                  onChange={(e) => handleInputChange('loanType', e.target.value)}
                  className={errors.loanType ? 'error' : ''}
                  disabled={isFieldDisabled('loanType')}
                >
                  <option value="SENIOR">Senior Mortgage</option>
                  <option value="MEZZANINE">Mezzanine</option>
                  <option value="BRIDGE">Bridge</option>
                  <option value="CONSTRUCTION">Construction</option>
                  <option value="PERMANENT">Permanent</option>
                  <option value="CMBS">CMBS</option>
                </Select>
                {errors.loanType && <FieldError>{errors.loanType}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="instrumentSubtype">Instrument Subtype*</Label>
                <Select
                  id="instrumentSubtype"
                  value={formData.instrumentSubtype || 'WHOLE_LOAN'}
                  onChange={(e) => {
                    const next = e.target.value;
                    handleInputChange('instrumentSubtype', next);
                    // Auto-derive loanType / construction flag from subtype.
                    if (next === 'CONSTRUCTION') {
                      handleInputChange('loanType', 'CONSTRUCTION');
                      handleInputChange('isConstruction', 'true');
                    } else if (next === 'MEZZANINE') {
                      handleInputChange('loanType', 'MEZZANINE');
                    } else if (next === 'BRIDGE_SENIOR' || next === 'BRIDGE_MEZZ') {
                      handleInputChange('loanType', 'BRIDGE');
                    } else if (next === 'PERMANENT') {
                      handleInputChange('loanType', 'PERMANENT');
                    } else if (next === 'CMBS_LEGACY') {
                      handleInputChange('loanType', 'CMBS');
                    } else if (next === 'WHOLE_LOAN' || next === 'PREFERRED_EQUITY') {
                      // SENIOR-equivalent defaults
                      if (formData.loanType === 'CONSTRUCTION') handleInputChange('loanType', 'SENIOR');
                    }
                    if (next !== 'CONSTRUCTION') handleInputChange('isConstruction', 'false');

                    // V299: auto-default typed classification discriminators based on subtype.
                    // Mirrors RealEstateLoan.defaultGaapFor / defaultCollateralFor in Java.
                    if (next === 'PREFERRED_EQUITY') {
                      handleInputChange('collateralType', 'OPERATING_AGREEMENT_PLEDGE');
                      handleInputChange('gaapClassification', 'PERMANENT_EQUITY');
                    } else if (next === 'MEZZANINE' || next === 'BRIDGE_MEZZ') {
                      handleInputChange('collateralType', 'EQUITY_PLEDGE');
                      handleInputChange('gaapClassification', 'MEZZANINE_EQUITY');
                    } else {
                      handleInputChange('collateralType', 'FIRST_MORTGAGE_LIEN');
                      handleInputChange('gaapClassification', 'DEBT');
                    }
                  }}
                  disabled={isFieldDisabled('instrumentSubtype')}
                >
                  <option value="WHOLE_LOAN">Whole Loan (senior)</option>
                  <option value="MEZZANINE">Mezzanine</option>
                  <option value="BRIDGE_SENIOR">Bridge — Senior</option>
                  <option value="BRIDGE_MEZZ">Bridge — Mezz</option>
                  <option value="CONSTRUCTION">Construction</option>
                  <option value="PERMANENT">Permanent</option>
                  <option value="PREFERRED_EQUITY">Preferred Equity</option>
                  <option value="A_NOTE">A-Note (split syndication)</option>
                  <option value="B_NOTE">B-Note (split syndication)</option>
                  <option value="CMBS_LEGACY">CMBS Tranche (legacy)</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="collateralType">Collateral Type</Label>
                <Select
                  id="collateralType"
                  value={formData.collateralType || 'FIRST_MORTGAGE_LIEN'}
                  onChange={(e) => handleInputChange('collateralType', e.target.value)}
                  disabled={isFieldDisabled('collateralType')}
                >
                  <option value="FIRST_MORTGAGE_LIEN">First Mortgage Lien</option>
                  <option value="SECOND_MORTGAGE_LIEN">Second Mortgage Lien</option>
                  <option value="EQUITY_PLEDGE">Equity Pledge (mezz)</option>
                  <option value="OPERATING_AGREEMENT_PLEDGE">OA Pledge (pref equity)</option>
                  <option value="INTERCREDITOR_SUBORDINATED">Intercreditor Subordinated</option>
                  <option value="UNSECURED">Unsecured</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="gaapClassification">GAAP Classification</Label>
                <Select
                  id="gaapClassification"
                  value={formData.gaapClassification || 'DEBT'}
                  onChange={(e) => handleInputChange('gaapClassification', e.target.value)}
                  disabled={isFieldDisabled('gaapClassification')}
                >
                  <option value="DEBT">Debt</option>
                  <option value="MEZZANINE_EQUITY">Mezzanine Equity</option>
                  <option value="PERMANENT_EQUITY">Permanent Equity</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="payMechanic">Pay Mechanic</Label>
                <Select
                  id="payMechanic"
                  value={formData.payMechanic || 'CASH_PAY'}
                  onChange={(e) => handleInputChange('payMechanic', e.target.value)}
                  disabled={isFieldDisabled('payMechanic')}
                >
                  <option value="CASH_PAY">Cash Pay</option>
                  <option value="PIK_ACCRUAL">PIK Accrual</option>
                  <option value="HYBRID">Hybrid (cash + PIK)</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="portfolio">Portfolio*</Label>
                <Select
                  id="portfolio"
                  value={formData.portfolio}
                  onChange={(e) => handleInputChange('portfolio', e.target.value)}
                  onBlur={(e) => handleInputBlur('portfolio', e.target.value)}
                  className={errors.portfolio ? 'error' : ''}
                  disabled={isFieldDisabled('portfolio')}
                >
                  <option value="">Select Portfolio</option>
                  {dimensionData.portfolios.map((p: any) => (
                    <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
                  ))}
                </Select>
                {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  disabled={isFieldDisabled('currency')}
                >
                  <option value="">Select Currency</option>
                  {dimensionData.currencies.map((c: any) => (
                    <option key={c.id} value={c.currencyCode}>
                      {c.currencyCode} - {c.currencyName}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormRow>

            {/* ── Counterparties ── */}
            <SectionTitle>Counterparties</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label htmlFor="sponsorName">Sponsor / Borrower</Label>
                <Input
                  id="sponsorName"
                  type="text"
                  value={formData.sponsorName}
                  onChange={(e) => handleInputChange('sponsorName', e.target.value)}
                  placeholder="e.g. Boston Properties LP"
                  disabled={isFieldDisabled('sponsorName')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="guarantorName">Guarantor</Label>
                <Input
                  id="guarantorName"
                  type="text"
                  value={formData.guarantorName}
                  onChange={(e) => handleInputChange('guarantorName', e.target.value)}
                  placeholder="e.g. parent recourse entity"
                  disabled={isFieldDisabled('guarantorName')}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="servicerName">Servicer</Label>
                <Input
                  id="servicerName"
                  type="text"
                  value={formData.servicerName}
                  onChange={(e) => handleInputChange('servicerName', e.target.value)}
                  placeholder="e.g. Wells Fargo CRE Servicing"
                  disabled={isFieldDisabled('servicerName')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="specialServicer">Special Servicer</Label>
                <Input
                  id="specialServicer"
                  type="text"
                  value={formData.specialServicer}
                  onChange={(e) => handleInputChange('specialServicer', e.target.value)}
                  placeholder="e.g. LNR Partners"
                  disabled={isFieldDisabled('specialServicer')}
                />
              </FormGroup>
            </FormRow>

            {/* ── Economics ── */}
            <SectionTitle>Economics</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label htmlFor="originalPrincipal">Original Principal ($)</Label>
                <Input
                  id="originalPrincipal"
                  type="text"
                  value={formData.originalPrincipal}
                  onChange={(e) => handleInputChange('originalPrincipal', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed > 0) {
                      setFormData((prev: any) => ({ ...prev, originalPrincipal: formatAmountWithCommas(parsed) }));
                    }
                  }}
                  placeholder="e.g. 55M, 55000000"
                  disabled={isFieldDisabled('originalPrincipal')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="totalCommitment">Total Commitment ($)</Label>
                <Input
                  id="totalCommitment"
                  type="text"
                  value={formData.totalCommitment}
                  onChange={(e) => handleInputChange('totalCommitment', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed > 0) {
                      setFormData((prev: any) => ({ ...prev, totalCommitment: formatAmountWithCommas(parsed) }));
                    }
                  }}
                  placeholder="e.g. 65M (≥ funded for construction loans)"
                  disabled={isFieldDisabled('totalCommitment')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="fundedAmount">Funded Amount ($)*</Label>
                <Input
                  id="fundedAmount"
                  type="text"
                  value={formData.fundedAmount}
                  onChange={(e) => handleInputChange('fundedAmount', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed > 0) {
                      const formatted = formatAmountWithCommas(parsed);
                      setFormData((prev: any) => ({
                        ...prev,
                        fundedAmount: formatted,
                        amount: formatted,
                      }));
                    }
                    handleInputBlur('fundedAmount', parsed.toString());
                  }}
                  placeholder="e.g. 55M, 55000000"
                  className={errors.fundedAmount ? 'error' : ''}
                  disabled={isFieldDisabled('fundedAmount')}
                />
                {errors.fundedAmount && <FieldError>{errors.fundedAmount}</FieldError>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="isConstruction">Construction Loan?</Label>
                <Select
                  id="isConstruction"
                  value={formData.isConstruction === 'true' || formData.isConstruction === true ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('isConstruction', e.target.value)}
                  disabled={isFieldDisabled('isConstruction')}
                >
                  <option value="false">No</option>
                  <option value="true">Yes</option>
                </Select>
              </FormGroup>

              {(formData.isConstruction === 'true' || formData.isConstruction === true) && (
                <FormGroup>
                  <Label htmlFor="constructionBudget">Construction Budget ($)</Label>
                  <Input
                    id="constructionBudget"
                    type="text"
                    value={formData.constructionBudget}
                    onChange={(e) => handleInputChange('constructionBudget', e.target.value)}
                    onBlur={(e) => {
                      const parsed = parseAmountShorthand(e.target.value);
                      if (parsed > 0) {
                        setFormData((prev: any) => ({ ...prev, constructionBudget: formatAmountWithCommas(parsed) }));
                      }
                    }}
                    placeholder="e.g. 80M"
                    disabled={isFieldDisabled('constructionBudget')}
                  />
                </FormGroup>
              )}
            </FormRow>

            {/* ── Rate Structure ── */}
            <SectionTitle>Rate Structure</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label htmlFor="rateType">Rate Type*</Label>
                <Select
                  id="rateType"
                  value={formData.rateType}
                  onChange={(e) => handleInputChange('rateType', e.target.value)}
                  disabled={isFieldDisabled('rateType')}
                >
                  <option value="FLOATING">Floating</option>
                  <option value="FIXED">Fixed</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="interestRate">
                  {formData.rateType === 'FIXED' ? 'Fixed Rate (%)*' : 'All-In Rate (%) — at origination'}
                </Label>
                <Input
                  id="interestRate"
                  type="number"
                  step="0.0001"
                  value={formData.interestRate}
                  onChange={(e) => handleInputChange('interestRate', e.target.value)}
                  placeholder="e.g. 6.85"
                  className={errors.interestRate ? 'error' : ''}
                  disabled={isFieldDisabled('interestRate')}
                />
                {errors.interestRate && <FieldError>{errors.interestRate}</FieldError>}
              </FormGroup>
            </FormRow>

            {formData.rateType === 'FLOATING' && (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="indexRate">Index*</Label>
                  <Select
                    id="indexRate"
                    value={formData.indexRate}
                    onChange={(e) => handleInputChange('indexRate', e.target.value)}
                    className={errors.indexRate ? 'error' : ''}
                    disabled={isFieldDisabled('indexRate')}
                  >
                    <option value="SOFR">SOFR</option>
                    <option value="TERM_SOFR_1M">Term SOFR (1M)</option>
                    <option value="TERM_SOFR_3M">Term SOFR (3M)</option>
                    <option value="DAILY_SOFR">Daily SOFR (in arrears)</option>
                    <option value="LIBOR">LIBOR (legacy)</option>
                    <option value="PRIME">Prime</option>
                  </Select>
                  {errors.indexRate && <FieldError>{errors.indexRate}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="spreadBps">Spread (bps)*</Label>
                  <Input
                    id="spreadBps"
                    type="number"
                    step="1"
                    value={formData.spreadBps}
                    onChange={(e) => handleInputChange('spreadBps', e.target.value)}
                    placeholder="e.g. 275"
                    className={errors.spreadBps ? 'error' : ''}
                    disabled={isFieldDisabled('spreadBps')}
                  />
                  {errors.spreadBps && <FieldError>{errors.spreadBps}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="floorRate">Floor Rate (%)</Label>
                  <Input
                    id="floorRate"
                    type="number"
                    step="0.0001"
                    value={formData.floorRate}
                    onChange={(e) => handleInputChange('floorRate', e.target.value)}
                    placeholder="e.g. 4.50"
                    disabled={isFieldDisabled('floorRate')}
                  />
                </FormGroup>
              </FormRow>
            )}

            {/* ── Dates ── */}
            <SectionTitle>Dates</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label htmlFor="originationDate">Origination Date*</Label>
                <DateField
                  id="originationDate"
                  value={formData.originationDate ?? ''}
                  className={errors.originationDate ? 'error' : ''}
                  disabled={isFieldDisabled('originationDate')}
                  onChange={iso => handleInputChange('originationDate', iso)}
                />
                {errors.originationDate && <FieldError>{errors.originationDate}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="maturityDate">Maturity Date*</Label>
                <DateField
                  id="maturityDate"
                  value={formData.maturityDate ?? ''}
                  className={errors.maturityDate ? 'error' : ''}
                  disabled={isFieldDisabled('maturityDate')}
                  onChange={iso => handleInputChange('maturityDate', iso)}
                />
                {errors.maturityDate && <FieldError>{errors.maturityDate}</FieldError>}
              </FormGroup>

              {(formData.isConstruction === 'true' || formData.isConstruction === true) && (
                <FormGroup>
                  <Label htmlFor="completionDate">Completion Date</Label>
                  <DateField
                    id="completionDate"
                    value={formData.completionDate ?? ''}
                    disabled={isFieldDisabled('completionDate')}
                    onChange={iso => handleInputChange('completionDate', iso)}
                  />
                </FormGroup>
              )}
            </FormRow>

            <FormRow>
              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="extensionOptions">Extension Options</Label>
                <Input
                  id="extensionOptions"
                  type="text"
                  value={formData.extensionOptions}
                  onChange={(e) => handleInputChange('extensionOptions', e.target.value)}
                  placeholder="e.g. Two 12-month extensions @ 25 bps fee, DSCR ≥ 1.20x"
                  disabled={isFieldDisabled('extensionOptions')}
                />
              </FormGroup>
            </FormRow>

            {/* ── Covenants ── */}
            <SectionTitle>Covenants</SectionTitle>
            <FormRow>
              <FormGroup>
                <Label htmlFor="maxLtvCovenant">Max LTV Covenant (%)</Label>
                <Input
                  id="maxLtvCovenant"
                  type="number"
                  step="0.01"
                  value={formData.maxLtvCovenant}
                  onChange={(e) => handleInputChange('maxLtvCovenant', e.target.value)}
                  placeholder="e.g. 70"
                  disabled={isFieldDisabled('maxLtvCovenant')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="minDscrCovenant">Min DSCR Covenant (x)</Label>
                <Input
                  id="minDscrCovenant"
                  type="number"
                  step="0.01"
                  value={formData.minDscrCovenant}
                  onChange={(e) => handleInputChange('minDscrCovenant', e.target.value)}
                  placeholder="e.g. 1.25"
                  disabled={isFieldDisabled('minDscrCovenant')}
                />
              </FormGroup>
            </FormRow>

            {/* ── Property Collateral ── */}
            <SectionTitle>Property Collateral</SectionTitle>
            <FormRow>
              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="propertyName">Property Name*</Label>
                <Input
                  id="propertyName"
                  type="text"
                  value={formData.propertyName}
                  onChange={(e) => handleInputChange('propertyName', e.target.value)}
                  placeholder="e.g. 100 Federal Street, Boston"
                  className={errors.propertyName ? 'error' : ''}
                  disabled={isFieldDisabled('propertyName')}
                />
                {errors.propertyName && <FieldError>{errors.propertyName}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="propertyType">Property Type*</Label>
                <Select
                  id="propertyType"
                  value={formData.propertyType}
                  onChange={(e) => handleInputChange('propertyType', e.target.value)}
                  className={errors.propertyType ? 'error' : ''}
                  disabled={isFieldDisabled('propertyType')}
                >
                  <option value="">Select Type</option>
                  <option value="OFFICE">Office</option>
                  <option value="MULTIFAMILY">Multifamily</option>
                  <option value="RETAIL">Retail</option>
                  <option value="INDUSTRIAL">Industrial</option>
                  <option value="HOTEL">Hotel</option>
                  <option value="HOSPITALITY">Hospitality</option>
                  <option value="MIXED_USE">Mixed Use</option>
                  <option value="SELF_STORAGE">Self Storage</option>
                  <option value="HEALTHCARE">Healthcare</option>
                  <option value="DATA_CENTER">Data Center</option>
                </Select>
                {errors.propertyType && <FieldError>{errors.propertyType}</FieldError>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup style={{ flex: 2 }}>
                <Label htmlFor="propertyAddress">Property Address</Label>
                <Input
                  id="propertyAddress"
                  type="text"
                  value={formData.propertyAddress}
                  onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
                  placeholder="e.g. 100 Federal Street"
                  disabled={isFieldDisabled('propertyAddress')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="propertyCity">City</Label>
                <Input
                  id="propertyCity"
                  type="text"
                  value={formData.propertyCity}
                  onChange={(e) => handleInputChange('propertyCity', e.target.value)}
                  placeholder="e.g. Boston"
                  disabled={isFieldDisabled('propertyCity')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="propertyState">State</Label>
                <Input
                  id="propertyState"
                  type="text"
                  value={formData.propertyState}
                  onChange={(e) => handleInputChange('propertyState', e.target.value)}
                  placeholder="e.g. MA"
                  disabled={isFieldDisabled('propertyState')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="propertyZip">ZIP</Label>
                <Input
                  id="propertyZip"
                  type="text"
                  value={formData.propertyZip}
                  onChange={(e) => handleInputChange('propertyZip', e.target.value)}
                  placeholder="e.g. 02110"
                  disabled={isFieldDisabled('propertyZip')}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="squareFootage">Square Footage</Label>
                <Input
                  id="squareFootage"
                  type="number"
                  value={formData.squareFootage}
                  onChange={(e) => handleInputChange('squareFootage', e.target.value)}
                  placeholder="e.g. 850000"
                  disabled={isFieldDisabled('squareFootage')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="unitCount">Unit Count (multifamily / hotel rooms)</Label>
                <Input
                  id="unitCount"
                  type="number"
                  value={formData.unitCount}
                  onChange={(e) => handleInputChange('unitCount', e.target.value)}
                  placeholder="e.g. 220"
                  disabled={isFieldDisabled('unitCount')}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="appraisedValue">Appraised Value ($)</Label>
                <Input
                  id="appraisedValue"
                  type="text"
                  value={formData.appraisedValue}
                  onChange={(e) => handleInputChange('appraisedValue', e.target.value)}
                  onBlur={(e) => {
                    const parsed = parseAmountShorthand(e.target.value);
                    if (parsed > 0) {
                      setFormData((prev: any) => ({ ...prev, appraisedValue: formatAmountWithCommas(parsed) }));
                    }
                  }}
                  placeholder="e.g. 95M, 95000000"
                  disabled={isFieldDisabled('appraisedValue')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="appraisalDate">Appraisal Date</Label>
                <DateField
                  id="appraisalDate"
                  value={formData.appraisalDate ?? ''}
                  disabled={isFieldDisabled('appraisalDate')}
                  onChange={iso => handleInputChange('appraisalDate', iso)}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="appraiser">Appraiser</Label>
                <Input
                  id="appraiser"
                  type="text"
                  value={formData.appraiser}
                  onChange={(e) => handleInputChange('appraiser', e.target.value)}
                  placeholder="e.g. CBRE, JLL, Cushman"
                  disabled={isFieldDisabled('appraiser')}
                />
              </FormGroup>
            </FormRow>

            {/* ── Loan Structure (subtype-conditional tabs) ── */}
            <SectionTitle>Loan Structure</SectionTitle>
            <LoanStructureTabs
              formData={formData}
              setFormData={setFormData}
              disabled={isFieldDisabled('instrumentSubtype')}
            />
              </StructuredBand>

              <EconomicsPreview
                toneName="cml"
                title="Live Debt Economics"
                notionalLabel="Funded Amount"
                notionalValue={fundedAmount ?? 0}
                price={100}
                consideration={fundedAmount ?? 0}
                currency={formData.currency || 'USD'}
                extraMetrics={[
                  { label: 'Origination LTV', value: ltv != null ? `${ltv.toFixed(1)}%` : '-' },
                  { label: 'All-In Coupon', value: allInRate != null ? `${allInRate.toFixed(2)}%` : '-' },
                  { label: 'Rate Type', value: formData.rateType || '-' },
                  { label: 'Maturity', value: formData.maturityDate || '-' },
                ]}
              />
            </TradeEconomicsGrid>

            {/* ── Calculated Metrics Panel ── */}
            {showMetrics && (
              <MetricsPanel>
                <SectionTitle style={{ marginTop: 0 }}>Calculated Metrics</SectionTitle>
                <MetricsGrid>
                  {ltv != null && (
                    <MetricCard $alert={ltv > 75}>
                      <MetricLabel>Origination LTV</MetricLabel>
                      <MetricValue $color={ltv > 75 ? '#d97706' : '#198754'}>
                        {ltv.toFixed(1)}%
                      </MetricValue>
                      <MetricSub>{ltv > 75 ? 'High leverage' : 'Within range'}</MetricSub>
                    </MetricCard>
                  )}
                  {allInRate != null && (
                    <MetricCard>
                      <MetricLabel>All-In Coupon</MetricLabel>
                      <MetricValue $color="#0d6efd">
                        {allInRate.toFixed(2)}%
                      </MetricValue>
                      <MetricSub>
                        {formData.rateType === 'FIXED'
                          ? 'Fixed coupon'
                          : `${formData.indexRate} + ${formData.spreadBps || 0} bps`}
                      </MetricSub>
                    </MetricCard>
                  )}
                  {fundedAmount != null && (
                    <MetricCard>
                      <MetricLabel>Funded Principal</MetricLabel>
                      <MetricValue>
                        ${(fundedAmount / 1e6).toFixed(1)}M
                      </MetricValue>
                      <MetricSub>Posted as quantity @ par (price 100)</MetricSub>
                    </MetricCard>
                  )}
                </MetricsGrid>
              </MetricsPanel>
            )}
          </StructuredWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default RealEstateTicketScreen;
